'use client'

import { UserRoundCog } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import { type AppBrandKey, getAppBrand } from '../logos'
import { Button } from './button'
import { Card } from './card'
import { Field, FieldLabel } from './field'
import { Input } from './input'
import { PasswordInput } from './password-input'

export type SigninCardFullMode = 'magic' | 'password'

export interface SigninCardFullCallbackResult {
  ok: boolean
  /** When ok=true, an optional path the consumer wants to navigate to. */
  redirectTo?: string
  /** When ok=false, a user-visible error message. */
  error?: string
}

export interface SigninCardFullProps {
  appKey: AppBrandKey
  /**
   * Optional brand logo rendered small above the "Log in" title (the
   * consumer supplies it, e.g. `<OrdoLogo className="h-6 w-auto" />`, so
   * the primitive stays brand-agnostic). When set, it replaces the
   * "Continue to {brand}" subtitle.
   */
  logo?: React.ReactNode
  /**
   * href the Google button points at — typically `/api/auth/login`. The
   * current `pathname + search` is appended as `?next=…` so the user
   * lands back where they came from after the multipass round-trip.
   *
   * OMIT to hide the Google button entirely. `apps/ordo` omits it: Google
   * OAuth only resolves internal Ordo accounts, and ordo's users are
   * mostly external, so the button was a dead end for nearly everyone
   * who saw it.
   */
  loginHref?: string
  onSendMagicLink: (email: string) => Promise<SigninCardFullCallbackResult>
  onSignInWithCredentials: (email: string, password: string) => Promise<SigninCardFullCallbackResult>
  /** Path the "Forgot password?" link points at. Default: `/forgot-password`. */
  forgotPasswordHref?: string
  /**
   * Destination for the "New to {brand}? Get started" link. When omitted
   * the link is hidden. (The full school-first signup funnel is a
   * separate surface — see apps/ordo signup plans.)
   */
  getStartedHref?: string
  /** Initial mode. Default: `magic`. */
  initialMode?: SigninCardFullMode
  /**
   * Seed the email field (e.g. from an invite link's `?email=` query
   * param). The field stays editable; this is a starting value only.
   */
  initialEmail?: string
  /**
   * Server-rendered error message (e.g. `?error=denied` after an OAuth
   * round-trip). Renders above the active form. Caller parses the query.
   */
  errorMessage?: string | null
  /** Optional className appended to the outer wrapper. */
  className?: string
}

/**
 * Multi-method sign-in card for customer-facing Ordo apps (apps/ordo).
 *
 * Layout (per the approved login mockup):
 *   "Log in" / "Continue to {brand}"
 *   Email → dark "Send sign-in link"  (magic-link first)
 *     ↳ after submit: "If the email is on file, we sent a sign-in
 *       link." + "Use a different email"
 *   — or —
 *   "Sign in with password" (secondary, switches to the password form)
 *   [Google]                      (opt-in — only when `loginHref` is
 *                                   passed. Apple/Facebook pending
 *                                   backend OAuth providers)
 *   "New to {brand}? Get started →"
 *
 * Form state lives entirely inside this primitive; the consumer passes
 * async callbacks for each submission.
 */
export function SigninCardFull({
  appKey,
  logo,
  loginHref,
  onSendMagicLink,
  onSignInWithCredentials,
  forgotPasswordHref = '/forgot-password',
  getStartedHref,
  initialMode = 'magic',
  initialEmail,
  errorMessage,
  className,
}: SigninCardFullProps) {
  const brand = getAppBrand(appKey)
  const [mode, setMode] = React.useState<SigninCardFullMode>(initialMode)
  const [email, setEmail] = React.useState(initialEmail ?? '')
  const [password, setPassword] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [magicLinkSent, setMagicLinkSent] = React.useState(false)
  const [localError, setLocalError] = React.useState<string | null>(null)
  const emailRef = React.useRef<HTMLInputElement>(null)
  const googleHref = useNextAwareHref(loginHref)

  if (!brand) {
    return null
  }

  async function handleMagicLinkSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    setLocalError(null)
    const result = await onSendMagicLink(email.trim())
    setSubmitting(false)
    if (result.ok) {
      setMagicLinkSent(true)
    } else {
      setLocalError(result.error ?? 'Something went wrong. Please try again.')
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    setLocalError(null)
    const result = await onSignInWithCredentials(email.trim(), password)
    setSubmitting(false)
    if (result.ok) {
      if (result.redirectTo && typeof window !== 'undefined') {
        window.location.href = result.redirectTo
      }
    } else {
      setLocalError(result.error ?? 'Invalid email or password.')
    }
  }

  function resetToMagic() {
    setMode('magic')
    setLocalError(null)
    setMagicLinkSent(false)
    setPassword('')
  }

  const surfaceError = errorMessage ?? localError

  return (
    <Card className={cn('flex w-full max-w-md flex-col gap-6 p-8 shadow-md', className)}>
      <header className="flex flex-col gap-2">
        {logo ? <div className="text-foreground [&>svg]:h-6 [&>svg]:w-auto">{logo}</div> : null}
        <h1 className="text-2xl font-semibold tracking-tight">Log in</h1>
      </header>

      {surfaceError ? (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {surfaceError}
        </div>
      ) : null}

      {/* Top section: magic entry / sent confirmation / password form */}
      {mode === 'password' ? (
        <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
          <Field>
            <FieldLabel htmlFor="signin-email">Email</FieldLabel>
            <Input
              id="signin-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="signin-password">Password</FieldLabel>
            <PasswordInput
              id="signin-password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
          <a href={forgotPasswordHref} className="text-center text-xs text-muted-foreground hover:underline">
            Forgot password?
          </a>
        </form>
      ) : magicLinkSent ? (
        <div className="rounded-lg border border-border bg-muted/40 p-4 text-center text-sm">
          <p>If the email is on file, we sent a sign-in link.</p>
          <button
            type="button"
            onClick={resetToMagic}
            className="mt-3 text-sm font-medium text-primary hover:underline">
            Use a different email
          </button>
        </div>
      ) : (
        <form onSubmit={handleMagicLinkSubmit} className="flex flex-col gap-3">
          <Field>
            <FieldLabel htmlFor="signin-email">Email</FieldLabel>
            <Input
              id="signin-email"
              ref={emailRef}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </Field>
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'Sending…' : 'Send sign-in link'}
          </Button>
        </form>
      )}

      {/* "or" divider */}
      <div className="relative">
        <hr className="border-border" />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
          or
        </span>
      </div>

      {/* Alternate methods. The secondary greys are the same hue as the
          card, so a /80 hover is invisible — `hover:brightness-95` gives a
          clear, clickable darken instead. */}
      <div className="flex flex-col gap-2">
        {mode === 'password' ? (
          <Button
            type="button"
            variant="secondary"
            className="w-full transition hover:brightness-95"
            onClick={resetToMagic}>
            Use a sign-in link instead
          </Button>
        ) : (
          <Button
            type="button"
            variant="secondary"
            className="w-full gap-2 transition hover:brightness-95"
            onClick={() => {
              setMode('password')
              setLocalError(null)
              setMagicLinkSent(false)
            }}>
            <UserRoundCog className="h-4 w-4" />
            Sign in with password
          </Button>
        )}

        {/* Google — opt-in: rendered only when the consumer passes
              `loginHref`. Wired to multipass OAuth. (Apple/Facebook
              intentionally omitted: backend supports Google only.) Rendered
              as a Button (asChild) so it carries data-slot="button" and
              inherits the host app's button radius, matching the two
              Buttons above. */}
        {googleHref ? (
          <Button asChild variant="secondary" className="w-full gap-2 transition hover:brightness-95 [&_svg]:size-5">
            <a href={googleHref}>
              <GoogleGlyph className="h-5 w-5" />
              Continue with Google
            </a>
          </Button>
        ) : null}
      </div>

      <p className="text-sm text-muted-foreground">
        New to {brand.name}?{' '}
        {getStartedHref ? (
          <a href={getStartedHref} className="font-medium text-primary hover:underline">
            Get started →
          </a>
        ) : (
          // Interim: until the school-first signup funnel ships, "Get started"
          // drops the visitor into the magic-link entry (the front door for
          // a brand-new email).
          <button
            type="button"
            onClick={() => {
              resetToMagic()
              emailRef.current?.focus()
            }}
            className="font-medium text-primary hover:underline">
            Get started →
          </button>
        )}
      </p>
    </Card>
  )
}

function useNextAwareHref(loginHref: string | undefined): string | undefined {
  const [href, setHref] = React.useState(loginHref)
  React.useEffect(() => {
    if (!loginHref || typeof window === 'undefined') {
      return
    }
    const current = `${window.location.pathname}${window.location.search}`
    if (!current || current === '/signin' || current === '/login' || current === '/') {
      setHref(loginHref)
      return
    }
    const sep = loginHref.includes('?') ? '&' : '?'
    setHref(`${loginHref}${sep}next=${encodeURIComponent(current)}`)
  }, [loginHref])
  return href
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path d="M5.84 14.1a6.97 6.97 0 0 1 0-4.2V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" fill="#FBBC05" />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
        fill="#EA4335"
      />
    </svg>
  )
}
